import { useReportEffect } from "../../../../hooks/useReportEffect";
import { getBatchCategories } from "../../../../services/batchReportService";
import { ReportTop } from "../../../../components/Dashboard/ReportLayout";
import { ReactComponent as CattleMap } from "../../../../assets/svg/icons/cattle.svg";
import { useFilter } from "../../../../hooks/useFilter";
import Constants from "../../../../constants/Index";
import { MultiDropdownV2 } from "../../../../components/FormControlsV2";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles.css";
import { IBatch } from "../../../../@types/API/IBatches";
import { IAreas } from "../../../../@types/API/IArea";
import { IOptionProps } from "../../../../@types/reactSelect/IOption";

import { exportBatchesDataToExcel } from "../../../../utils/excel/exportBatchesData";
import { sortBy } from "../../../../utils/arrays/sortBy";
import DownloadChip from "../../../../components/Chips/ComponentChipDownload/Index";
import {
  selectionToNumericIds,
  selectionToSortedIdsKey,
  useFilterContextV2,
} from "../../../../contexts/FilterContextV2";

export default function DashboardCattleMapBatches() {
  const { t } = useTranslation();
  const {
    setHideMicroAreaFilter,
    setHideBatchFilter,
    setDashboard,
    setSideBarOptionSelected,
    batchCategory,
    setBatchCategory
  } = useFilter();

  const { selectedFarms, selectedAreas } = useFilterContextV2();

  const farmsKey = useMemo(
    () => selectionToSortedIdsKey(selectedFarms),
    [selectedFarms],
  );
  const areasKey = useMemo(
    () => selectionToSortedIdsKey(selectedAreas),
    [selectedAreas],
  );

  const {
    control,
    setValue,
    watch
  } = useForm();

  const [areas, setAreas] = useState<IAreas>();
  const [batches, setBatches] = useState<IBatch[]>();
  const batchesSelected = watch("batches");
  const batchesKey = selectionToSortedIdsKey(batchesSelected);

  useEffect(() => {
    setHideMicroAreaFilter(true)
    setHideBatchFilter(true)
    setSideBarOptionSelected("DASHBOARD")
    setDashboard("BATCHES")
    document.title = Constants.TITLE_DASHBOARD;
  }, []);

  const farmIds = useMemo(
    () => selectionToNumericIds(selectedFarms),
    [selectedFarms],
  );
  const selectedAreaIds = useMemo(
    () => selectionToNumericIds(selectedAreas),
    [selectedAreas],
  );

  useReportEffect((axios) => {
    setAreas(undefined);
    setBatches(undefined);
    if (!farmIds.length) {
      setBatchCategory(undefined);
      setValue("batches", []);
      return;
    }
    const controller = new AbortController();
    Promise.all(farmIds.map(farmId => axios.get<IAreas>(`/areas?farm_id=${farmId}`, { signal: controller.signal })))
      .then(responses => setAreas(Array.from(new Map(responses.flatMap(response => response.data).map(area => [area.id, area])).values())))
      .catch(() => undefined);
    Promise.all(farmIds.map(farmId => axios.get<IBatch[]>(`/batches?farm_id=${farmId}`, { signal: controller.signal })))
      .then(responses => setBatches(Array.from(new Map(responses.flatMap(response => response.data).map(batch => [batch.id, batch])).values())))
      .catch(() => undefined);
    return () => controller.abort();
  }, [farmsKey, setValue, setBatchCategory]);

  const batchOptions = useMemo(() => (batches ?? [])
    .filter(batch => batch.status === "ACTIVE" && selectedAreaIds.includes(Number(batch.area.id)))
    .map(batch => ({ value: batch.id, label: `${batch.farm.initials}-${batch.area.initials}-${batch.initials}` }))
    .sort(sortBy("label")), [batches, areasKey]);

  useEffect(() => {
    if (!batches) return;
    const valid = (batchesSelected ?? []).filter((batch: IOptionProps) => batchOptions.some(option => String(option.value) === String(batch.value)));
    if (valid.length !== (batchesSelected ?? []).length) setValue("batches", valid);
  }, [batches, batchOptions, batchesSelected, setValue]);

  useReportEffect((axios) => {
    if (!farmIds.length || !selectedAreaIds.length) { setBatchCategory(undefined); return; }
    if (!areas || !batches) return;
    const areaIds = selectedAreaIds.filter(id => areas.some(area => Number(area.id) === id));
    if (!areaIds.length) { setBatchCategory(undefined); return; }
    const batchIds = (batchesSelected ?? [])
      .filter((batch: IOptionProps) => batchOptions.some(option => String(option.value) === String(batch.value)))
      .map((batch: IOptionProps) => Number(batch.value));
    const controller = new AbortController();
    getBatchCategories({ farmIds, areaIds, batchIds }, controller.signal, axios)
      .then(setBatchCategory)
      .catch(() => undefined);
    return () => controller.abort();
  }, [farmsKey, areasKey, areas, batches, batchesKey, batchOptions, setBatchCategory]);

  return (
    <div className="cattleMap_background cattleMap_batchesPrint" style={{ height: "100vh", overflowY: "auto" }}>
      <form className="dashboardPastureManagement">

        <div className="cattleMap_page">
          <ReportTop kind="batches" icon={<CattleMap height={72} />} title={t("reports.cattleMap")} subtitle={t("reports.batchView")}>
            <div style={{ width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
              <Controller
                name="batches"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <MultiDropdownV2
                    name="batches"
                    label={t("reports.batches")}
                    placeholder={t("reportLayout.allBatches")}
                    printEmptyLabel={t("reportLayout.allBatches")}
                    menuMount="overlay"
                    emptyMessage={t("reports.noOptions")}
                    options={batchOptions}
                    selectAll
                    value={field.value ?? []}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={error?.message}
                  />
                )}
              />

              <DownloadChip
                enableExcel
                enablePdf
                onExcelSelect={() => {
                  try {
                    exportBatchesDataToExcel(batchCategory);
                  } catch (error) {
                    console.error('Erro ao exportar:', error);
                    alert(t("reports.exportError"));
                  }
                }}
                onPdfSelect={() => {
                  window.print()
                }}
              />
            </div>
          </ReportTop>

          <div className="tableDashboard">
            <table>
              <thead style={{
                color: "#fff"
              }}>
                <tr>
                  <th scope="col" rowSpan={2} style={{
                    background: "#324440",
                    width: "150px",
                    textAlign: "left"
                  }}>
                    {t("reports.batches")}
                  </th>
                  <th scope="col" rowSpan={2} style={{
                    background: "#324440",
                    width: "150px",
                    textAlign: "left"
                  }}>
                    {t("reports.paddock")}
                  </th>
                  <th scope="col" colSpan={6} style={{
                    textAlign: "center",
                    background: "#62b8ae"
                  }}>
                    {t("reports.male")}
                  </th>
                  <th scope="col" colSpan={5} style={{
                    textAlign: "center",
                    background: "#ff6161"
                  }}>
                    {t("reports.female")}
                  </th>
                  <th scope="col" rowSpan={2} style={{
                    background: "#57CE71",
                    width: "80px"
                  }}>
                    {t("reports.balanceLabel")}
                  </th>
                </tr>
                <tr>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    0 a 8 {t("reports.months")}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    9 a 12 {t("reports.months")}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    13 a 24 {t("reports.months")}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    25 a 36 {t("reports.months")}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    {`> 37 ${t("reports.months")}`}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    {t("reports.bulls")}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    0 a 8 {t("reports.months")}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    9 a 12 {t("reports.months")}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    13 a 24 {t("reports.months")}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    25 a 36 {t("reports.months")}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    {`> 37 ${t("reports.months")}`}
                  </th>
                </tr>
              </thead>

              <tbody>
                {batchCategory && batchCategory.content.map((batch, index) => (
                  <tr
                    key={`${batch.farm_initials}-${batch.area_initials}-${batch.initials}-${index}`}
                    className={index % 2 === 0 ? "bg_white" : "bg_gray"}
                  >
                    <th scope="row">{`${batch.farm_initials}-${batch.area_initials}-${batch.initials}`}</th>
                    <td>
                      {`${batch.farm_initials}-${batch.area_initials}-${batch.micro_area_initials}`}
                    </td>
                    {[6, 7, 8, 9, 10, 11].map((id) => (
                      <td key={`male-${id}`}>
                        {batch.categories.find((category) => category.id === id)?.quantity || 0}
                      </td>
                    ))}
                    {[1, 2, 3, 4, 5].map((id) => (
                      <td key={`female-${id}`}>
                        {batch.categories.find((category) => category.id === id)?.quantity || 0}
                      </td>
                    ))}
                    <td>
                      {batch.categories.reduce((sum, category) => sum + category.quantity, 0)}
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot
                style={{
                  background: "#324440",
                  color: "#fff"
                }}
              >
                <tr>
                  <th style={{
                    textAlign: "left"
                  }}>{t("reports.total")}</th>
                  <td/>
                  {[6, 7, 8, 9, 10, 11].map((id) => (
                    <td key={`male-${id}`}>
                      {batchCategory
                        ? batchCategory.content.reduce(
                          (sum, batch) =>
                            sum +
                            (batch.categories.find((category) => category.id === id)?.quantity || 0),
                          0
                        )
                        : 0}
                    </td>
                  ))}
                  {[1, 2, 3, 4, 5].map((id) => (
                    <td key={`female-${id}`}>
                      {batchCategory
                        ? batchCategory.content.reduce(
                          (sum, batch) =>
                            sum +
                            (batch.categories.find((category) => category.id === id)?.quantity || 0),
                          0
                        )
                        : 0}
                    </td>
                  ))}
                  <td>
                    {batchCategory
                      ? batchCategory.content.reduce(
                        (sum, batch) =>
                          sum +
                          batch.categories.reduce((batchSum, category) => batchSum + category.quantity, 0),
                        0
                      )
                      : 0}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </form>
    </div>
  );
}
